import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from './common/Button';
import api from '../services/api';

const INTERESTS = [
  'Painting', 'Sculpture', 'Digital Art', 'Design', 'Handcraft'
];

interface OnboardingModalProps {
  onComplete: () => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ onComplete }) => {
  const { user, fetchUser } = useAuth();
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleInterest = (interest: string) => {
    setSelectedInterests(prev => 
      prev.includes(interest)
        ? prev.filter(i => i !== interest)
        : [...prev, interest]
    );
  };

  const handleSubmit = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      await api.put(`/users/${user.id}/onboard`, {
        interests: selectedInterests
      });
      await fetchUser();
      onComplete();
    } catch (err: any) {
      console.error('Failed to save interests:', err);
      setError(err.response?.data?.error || 'Failed to save interests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-white rounded-[2rem] p-8 md:p-10 max-w-lg w-full shadow-2xl relative overflow-hidden"
        >
          {/* Decorative shapes */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500 rounded-full blur-[80px] opacity-20 -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500 rounded-full blur-[80px] opacity-20 translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative z-10 text-center mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 tracking-tight">Welcome to ANI! 🎉</h2>
            <p className="text-gray-500 text-lg font-light leading-relaxed">
              Let's personalize your experience. Select your favorite art categories to help us curate the best pieces for you.
            </p>
          </div>

          <div className="relative z-10 flex flex-wrap justify-center gap-3 mb-10">
            {INTERESTS.map(interest => {
              const isSelected = selectedInterests.includes(interest);
              return (
                <button
                  key={interest}
                  onClick={() => toggleInterest(interest)}
                  className={`px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 border-2 ${
                    isSelected 
                      ? 'bg-black border-black text-white shadow-lg transform scale-105' 
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {interest}
                </button>
              );
            })}
          </div>

          {error && (
            <div className="relative z-10 mb-6 p-4 bg-red-50 text-red-600 text-sm font-medium rounded-xl text-center">
              {error}
            </div>
          )}

          <div className="relative z-10">
            <Button
              onClick={handleSubmit}
              disabled={loading}
              fullWidth
              className="py-4 rounded-xl text-lg font-bold shadow-xl flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  Saving...
                </span>
              ) : selectedInterests.length === 0 ? (
                'Skip for now'
              ) : (
                'Start Exploring'
              )}
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default OnboardingModal;
